<template>
    <div class="container-fluid px-4 py-4 dukakit-send-receive" style="background:#f8fafc;min-height:100vh;">

        <!-- Header -->
        <div class="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-4" style="gap:12px;border-bottom:1px solid #e9ecef;">
            <div>
                <h4 class="text-primary mb-1" style="font-size:21px;font-weight:600;letter-spacing:-0.3px;">Send &amp; Receive Money</h4>
                <p class="text-secondary mb-0" style="font-size:13px;">Move money directly through your Dukakit business account.</p>
            </div>
            <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
                <button
                    class="btn btn-sm btn-light d-inline-flex align-items-center"
                    style="gap:6px;border-radius:8px;font-size:12px;border:1px solid #e2e8f0;"
                    @click="showReceiveInfo = !showReceiveInfo"
                >
                    <i class="bi bi-download"></i> Receive Money
                </button>
                <button
                    class="btn btn-sm btn-light d-inline-flex align-items-center"
                    style="gap:6px;border-radius:8px;font-size:12px;border:1px solid #e2e8f0;"
                    @click="openFundModal"
                >
                    <i class="bi bi-wallet2"></i> Fund Wallet
                </button>
                <button
                    class="btn btn-sm btn-dark d-inline-flex align-items-center"
                    style="gap:6px;border-radius:8px;font-size:12px;"
                    @click="openSendModal"
                >
                    <i class="bi bi-send"></i> Send Money
                </button>
            </div>
        </div>

        <!-- Wallet overview -->
        <div class="bg-white border mb-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px 24px;">
            <div class="text-secondary mb-3" style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.4px;">
                Wallet overview
            </div>
            <div class="row" style="row-gap:18px;">
                <div class="col-6 col-md-2" v-for="(tile, index) in statTiles" :key="tile.key">
                    <div class="text-secondary mb-1" style="font-size:12px;">{{ tile.label }}</div>
                    <div v-if="loading.stats" class="skeleton" style="height:22px;width:70%;border-radius:4px;"></div>
                    <div
                        v-else
                        class="text-dark"
                        :style="{ 'font-size': index === 0 ? '22px' : '17px', 'font-weight': 700, 'letter-spacing': '-0.2px' }"
                    >
                        KSh {{ tile.value }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Receive info -->
        <div v-if="showReceiveInfo" class="bg-white border mb-4" style="border-radius:14px;border-color:#e9ecef !important;padding:20px 24px;">
            <div class="text-dark mb-2" style="font-size:14px;font-weight:600;">Receive Payments</div>
            <div v-if="!status.enabled" class="text-secondary" style="font-size:13px;">
                Dukakit Payments is not enabled yet.
                <a href="/settings/payments">Enable it in Payment Settings</a>.
            </div>
            <div v-else class="d-flex flex-wrap align-items-center" style="gap:10px;">
                <div class="d-inline-flex align-items-center" style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#f8fafc;">
                    <span class="text-secondary" style="font-size:11px;">Paybill</span>
                    <span class="text-dark font-weight-bold" style="font-size:13px;">{{ status.paybill }}</span>
                </div>
                <div class="d-inline-flex align-items-center" style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#f8fafc;">
                    <span class="text-secondary" style="font-size:11px;">Account</span>
                    <span class="text-dark font-weight-bold" style="font-size:13px;">{{ status.account_number || 'Not assigned' }}</span>
                </div>
                <button
                    class="btn btn-sm btn-light"
                    style="border:1px solid #e2e8f0;border-radius:8px;font-size:12px;"
                    @click="copyPaybillInfo"
                >Copy details</button>
            </div>
        </div>

        <!-- Transactions -->
        <div class="bg-white border" style="border-radius:14px;border-color:#e9ecef !important;padding:20px 24px;">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:12px;">
                <span class="text-dark" style="font-size:14px;font-weight:600;">Transactions</span>
            </div>

            <!-- Filters -->
            <div class="d-flex flex-wrap align-items-center mb-3" style="gap:8px;">
                <div
                    class="d-inline-flex align-items-center"
                    style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#fff;min-width:260px;flex:1 1 260px;"
                >
                    <i class="bi bi-search text-secondary" style="font-size:12px;"></i>
                    <input
                        v-model="search"
                        type="text"
                        placeholder="Search name, phone, till, paybill, ref..."
                        style="border:none;background:transparent;font-size:12px;color:#0f172a;width:100%;"
                        class="dp-search-input"
                    />
                </div>

                <date-range-picker
                    v-model="dateRangeFilter"
                    :auto-apply="true"
                    :ranges="false"
                    opens="right"
                    :append-to-body="true"
                    class="custom-range-picker"
                >
                    <template #input="picker">
                        <span
                            class="d-inline-flex align-items-center"
                            style="border:1px solid #e2e8f0;border-radius:8px;padding:6px 10px;gap:6px;background:#fff;font-size:12px;font-weight:600;color:#0f172a;cursor:pointer;"
                        >
                            <i class="bi bi-calendar-event text-secondary"></i>
                            <template v-if="picker.startDate && picker.endDate">
                                {{ formatPickerDate(picker.startDate) }} – {{ formatPickerDate(picker.endDate) }}
                            </template>
                            <span v-else class="text-secondary" style="font-weight:500;">All dates</span>
                        </span>
                    </template>
                </date-range-picker>

                <button
                    v-if="search || (dateRangeFilter.startDate && dateRangeFilter.endDate)"
                    class="btn btn-sm btn-light"
                    style="border:1px solid #e2e8f0;border-radius:8px;font-size:12px;"
                    @click="clearFilters"
                >Clear filters</button>
            </div>

            <div v-if="loading.transactions" class="empty-state">
                <i class="bi bi-arrow-repeat spin"></i>
                <p class="mb-0 mt-2">Loading transactions...</p>
            </div>

            <div v-else-if="filteredTransactions.length === 0" class="empty-state">
                <i class="bi bi-receipt"></i>
                <p class="mb-0 mt-2">No transactions found.</p>
            </div>

            <div v-else class="table-container">
                <table class="dp-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Recipient / Source</th>
                            <th>Reference</th>
                            <th class="text-right">Amount</th>
                            <th class="text-right">Fees</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="tx in filteredTransactions" :key="tx.id" class="dp-row" @click="openDetailsModal(tx)">
                            <td><span class="text-secondary" style="font-size:12px;">{{ formatDate(tx.created_at) }}</span></td>
                            <td><span class="badge pill" :class="typeBadgeClass(tx.type)">{{ formatType(tx.type) }}</span></td>
                            <td><span class="text-dark" style="font-size:13px;font-weight:500;">{{ (tx.presented && tx.presented.counterparty) || '—' }}</span></td>
                            <td><span class="text-secondary" style="font-size:12px;">{{ (tx.presented && tx.presented.reference) || '—' }}</span></td>
                            <td class="text-right">
                                <span :class="tx.direction === 'credit' ? 'text-dark' : 'text-danger'" style="font-weight:700;font-size:13px;">
                                    {{ tx.direction === 'credit' ? '+' : '-' }}KSh {{ formatMoney(tx.amount) }}
                                </span>
                            </td>
                            <td class="text-right">
                                <span class="text-secondary" style="font-size:12px;">{{ formatFees(tx) }}</span>
                            </td>
                            <td><span class="badge pill" :class="statusBadgeClass(tx)">{{ (tx.presented && tx.presented.status) || tx.direction }}</span></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Send Money Modal -->
        <b-modal
            ref="sendModal"
            hide-header-close
            no-close-on-backdrop
            static
            body-class="p-4"
            footer-class="d-flex justify-content-end px-4 py-3 border-top"
            header-class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
        >
            <template #modal-header>
                <h6 class="font-weight-bold text-dark mb-0">Send Money</h6>
                <button
                    class="btn btn-light btn-sm border-0 d-flex align-items-center justify-content-center p-1"
                    style="width:30px;height:30px;border-radius:6px;"
                    @click="$refs.sendModal.hide()"
                >
                    <i class="fas fa-times text-secondary"></i>
                </button>
            </template>

            <div class="form-group mb-3">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    Recipient type
                </label>
                <select class="form-control form-control-sm" v-model="sendForm.recipient_type" @change="quote">
                    <option value="personal">M-Pesa number (personal)</option>
                    <option value="paybill">Paybill</option>
                    <option value="till">Till (Buy Goods)</option>
                </select>
            </div>

            <div class="form-group mb-3">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    {{ recipientLabel }}
                </label>
                <vue-tel-input
                    v-if="sendForm.recipient_type === 'personal'"
                    v-on:validate="validateRecipientNumber"
                    v-model="sendForm.recipient"
                ></vue-tel-input>
                <input v-else class="form-control form-control-sm" v-model="sendForm.recipient" />
                <small v-if="sendForm.recipient_type === 'personal' && recipientValidMessage" class="text-danger d-block mt-1">{{ recipientValidMessage }}</small>
            </div>

            <div class="form-group mb-3" v-if="sendForm.recipient_type === 'paybill' || sendForm.recipient_type === 'till'">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    Account reference
                </label>
                <input class="form-control form-control-sm" v-model="sendForm.account_reference" maxlength="13" />
                <small class="text-secondary d-block mt-1" style="font-size:11px;">The account/reference the receiving {{ sendForm.recipient_type === 'till' ? 'Till' : 'Paybill' }} expects.</small>
            </div>

            <div class="form-group mb-3">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    Amount (KSh)
                </label>
                <input class="form-control form-control-sm" type="number" min="1" v-model.number="sendForm.amount" @input="quote" />
            </div>

            <div class="form-group mb-3">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    Reason <span class="text-secondary" style="text-transform:none;font-weight:400;">(optional)</span>
                </label>
                <input class="form-control form-control-sm" v-model="sendForm.reason" />
            </div>

            <div v-if="sendQuote" class="bg-light border p-3 mb-3" style="border-radius:8px;font-size:13px;">
                <div class="d-flex justify-content-between mb-1"><span class="text-secondary">Recipient receives</span><strong class="text-dark">KSh {{ formatMoney(sendQuote.amount) }}</strong></div>
                <div class="d-flex justify-content-between mb-1"><span class="text-secondary">Safaricom fee</span><span class="text-dark">KSh {{ formatMoney(sendQuote.safaricom_cost) }}</span></div>
                <div class="d-flex justify-content-between"><span class="text-secondary">Dukakit fee</span><span class="text-dark">KSh {{ formatMoney(sendQuote.dukakit_fee) }}</span></div>
                <hr class="my-2">
                <div class="d-flex justify-content-between"><strong class="text-dark">Total required</strong><strong class="text-dark">KSh {{ formatMoney(sendQuote.total) }}</strong></div>
            </div>

            <div v-if="fundingRequired" class="bg-light border p-3 mb-3" style="border-radius:8px;">
                <p class="mb-2 text-dark" style="font-size:13px;">Insufficient wallet balance. KSh {{ fundingRequired }} is required. Enter a phone number to fund your wallet via STK Push, then Send Money will be retried automatically once funding is confirmed.</p>
                <vue-tel-input v-on:validate="validateFundingPhoneForSend" v-model="fundingPhoneForSend"></vue-tel-input>
                <small v-if="fundingPhoneForSendValidMessage" class="text-danger d-block mt-1 mb-2">{{ fundingPhoneForSendValidMessage }}</small>
                <button type="button" class="btn btn-dark btn-sm mt-2" :disabled="sending || !!fundingPhoneForSendValidMessage" @click="fundThenSend">Fund &amp; Send</button>
            </div>

            <template #modal-footer>
                <button type="button" class="btn btn-light btn-sm border font-weight-600 mr-2" @click="$refs.sendModal.hide()">Cancel</button>
                <button
                    type="button"
                    class="btn btn-dark btn-sm d-inline-flex align-items-center"
                    style="gap:6px;"
                    :disabled="sending"
                    @click="submitSend"
                >
                    <i v-if="sending" class="bi bi-arrow-repeat spin"></i>
                    {{ sending ? 'Sending...' : 'Send Money' }}
                </button>
            </template>
        </b-modal>

        <!-- Fund Wallet Modal -->
        <b-modal
            ref="fundModal"
            hide-header-close
            no-close-on-backdrop
            static
            body-class="p-4"
            footer-class="d-flex justify-content-end px-4 py-3 border-top"
            header-class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
        >
            <template #modal-header>
                <h6 class="font-weight-bold text-dark mb-0">Fund Wallet</h6>
                <button
                    class="btn btn-light btn-sm border-0 d-flex align-items-center justify-content-center p-1"
                    style="width:30px;height:30px;border-radius:6px;"
                    @click="$refs.fundModal.hide()"
                >
                    <i class="fas fa-times text-secondary"></i>
                </button>
            </template>

            <div class="form-group mb-3">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    Phone number
                </label>
                <vue-tel-input v-on:validate="validateFundPhoneNumber" v-model="fundForm.phone_number"></vue-tel-input>
                <small v-if="fundPhoneValidMessage" class="text-danger d-block mt-1">{{ fundPhoneValidMessage }}</small>
            </div>

            <div class="form-group mb-3">
                <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
                    Amount (KSh)
                </label>
                <input class="form-control form-control-sm" type="number" min="1" v-model.number="fundForm.amount" />
            </div>

            <small class="text-secondary d-block" style="font-size:12px;">You'll receive an M-Pesa STK prompt. Your wallet is credited once Safaricom confirms the payment.</small>

            <template #modal-footer>
                <button type="button" class="btn btn-light btn-sm border font-weight-600 mr-2" @click="$refs.fundModal.hide()">Cancel</button>
                <button
                    type="button"
                    class="btn btn-dark btn-sm d-inline-flex align-items-center"
                    style="gap:6px;"
                    :disabled="funding || !!fundPhoneValidMessage"
                    @click="submitFund"
                >
                    <i v-if="funding" class="bi bi-arrow-repeat spin"></i>
                    {{ funding ? 'Sending STK...' : 'Fund Wallet' }}
                </button>
            </template>
        </b-modal>

        <!-- Async Payment Progress Modal (Fund Wallet, Fund & Send, Send Money) -->
        <b-modal
            ref="dpProgressModal"
            id="modal-center-dukakit-progress"
            :hide-header="dpPollState === 'pending'"
            hide-footer
            :no-close-on-backdrop="dpPollState === 'pending'"
            :no-close-on-esc="dpPollState === 'pending'"
            centered
        >
            <div class="text-center pt-3">
                <template v-if="dpPollState === 'pending'">
                    <div class="spinner-border text-dark" role="status"><span class="sr-only">Loading...</span></div>
                    <p class="mt-2 text-dark" style="font-size:13px;">{{ dpPollPendingMessage }}</p>
                </template>
                <template v-else-if="dpPollState === 'completed'">
                    <div class="success-checkmark"><div class="check-icon">
                        <span class="icon-line line-tip"></span>
                        <span class="icon-line line-long"></span>
                        <div class="icon-circle"></div><div class="icon-fix"></div>
                    </div></div>
                    <p class="mt-2 text-dark" style="font-size:13px;">{{ dpPollMessage }}</p>
                </template>
                <template v-else-if="dpPollState === 'failed'">
                    <h5 class="text-danger"><i class="bi bi-x-circle"></i></h5>
                    <p class="text-dark" style="font-size:13px;">{{ dpPollMessage }}</p>
                    <button type="button" class="btn btn-dark btn-sm" @click="$refs.dpProgressModal.hide()">Close</button>
                </template>
            </div>
        </b-modal>

        <!-- Transaction Details Modal -->
        <b-modal
            ref="detailsModal"
            hide-header-close
            static
            body-class="p-4"
            footer-class="d-flex justify-content-end px-4 py-3 border-top"
            header-class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
        >
            <template #modal-header>
                <h6 class="font-weight-bold text-dark mb-0">Transaction details</h6>
                <button
                    class="btn btn-light btn-sm border-0 d-flex align-items-center justify-content-center p-1"
                    style="width:30px;height:30px;border-radius:6px;"
                    @click="$refs.detailsModal.hide()"
                >
                    <i class="fas fa-times text-secondary"></i>
                </button>
            </template>

            <div v-if="detailsLoading" class="text-center py-4 text-secondary">
                <i class="bi bi-arrow-repeat spin"></i> Loading...
            </div>
            <div v-else-if="selectedTx">
                <table class="detail-table">
                    <tbody>
                        <tr><th>Type</th><td>{{ formatType(selectedTx.type) }}</td></tr>
                        <tr><th>Date</th><td>{{ formatDate(selectedTx.created_at) }}</td></tr>
                        <tr><th>Amount</th><td>KSh {{ formatMoney(selectedTx.amount) }}</td></tr>
                        <tr><th>Safaricom Fee</th><td>{{ selectedTx.safaricom_cost ? 'KSh ' + formatMoney(selectedTx.safaricom_cost) : '—' }}</td></tr>
                        <tr><th>Dukakit Fee</th><td>{{ selectedTx.dukakit_fee ? 'KSh ' + formatMoney(selectedTx.dukakit_fee) : '—' }}</td></tr>
                        <tr v-for="row in selectedTx.details" :key="row.label">
                            <th>{{ row.label }}</th>
                            <td>
                                {{ row.value }}
                                <button type="button" class="btn btn-link btn-sm p-0 ml-1" title="Copy" @click="copyText(row.value)"><i class="bi bi-clipboard"></i></button>
                            </td>
                        </tr>
                        <tr v-if="!selectedTx.details || !selectedTx.details.length"><th>Recipient/Source</th><td>{{ selectedTx.counterparty || '—' }}</td></tr>
                        <tr><th>Status</th><td><span class="badge pill" :class="statusBadgeClass({ presented: selectedTx })">{{ selectedTx.status || selectedTx.direction }}</span></td></tr>
                        <tr v-if="selectedTx.note"><th>Note</th><td class="text-secondary">{{ selectedTx.note }}</td></tr>
                    </tbody>
                </table>
                <div class="text-right mt-2">
                    <button v-if="selectedTx.details && selectedTx.details.length" type="button" class="btn btn-light btn-sm border mr-2" @click="copyAllDetails(selectedTx)">Copy all details</button>
                </div>
            </div>

            <template #modal-footer>
                <button type="button" class="btn btn-light btn-sm border font-weight-600" @click="$refs.detailsModal.hide()">Close</button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
    name: 'SendReceiveMoney',

    components: {
        DateRangePicker,
    },

    data() {
        return {
            status: { enabled: false, paybill: '4109900', account_number: null },
            stats: {
                balance: '0.00', received_today: '0.00', sent_today: '0.00',
                pending_settlement: '0.00', settled_total: '0.00', fees_paid: '0.00',
            },
            transactions: [],
            search: '',
            dateRangeFilter: { startDate: null, endDate: null },
            showReceiveInfo: false,

            loading: { stats: false, transactions: false },

            sendForm: { recipient: '', amount: null, recipient_type: 'personal', account_reference: '', reason: '' },
            sendQuote: null,
            fundingRequired: null,
            fundingPhoneForSend: '',
            fundingPhoneForSendValidMessage: '',
            recipientValidMessage: '',
            sending: false,

            fundForm: { phone_number: '', amount: null },
            fundPhoneValidMessage: '',
            funding: false,

            dpPollInterval: null,
            dpPollState: 'pending',
            dpPollMessage: '',
            dpPollPendingMessage: '',

            selectedTx: null,
            detailsLoading: false,
        };
    },

    computed: {
        statTiles() {
            return [
                { key: 'balance', label: 'Available Balance', value: this.stats.balance },
                { key: 'received_today', label: 'Received Today', value: this.stats.received_today },
                { key: 'sent_today', label: 'Sent Today', value: this.stats.sent_today },
                { key: 'pending_settlement', label: 'Pending Settlement', value: this.stats.pending_settlement },
                { key: 'settled_total', label: 'Settled', value: this.stats.settled_total },
                { key: 'fees_paid', label: 'Fees Paid', value: this.stats.fees_paid },
            ];
        },
        filteredTransactions() {
            if (!this.search) return this.transactions;
            const term = this.search.toLowerCase();
            return this.transactions.filter(tx => {
                const haystack = [
                    tx.type,
                    this.formatType(tx.type),
                    tx.presented && tx.presented.counterparty,
                    tx.presented && tx.presented.reference,
                ].filter(Boolean).join(' ').toLowerCase();
                return haystack.includes(term);
            });
        },
        recipientLabel() {
            return {
                personal: 'Recipient phone number',
                paybill: 'Recipient Paybill number',
                till: 'Recipient Till number',
            }[this.sendForm.recipient_type] || 'Recipient';
        },
    },

    watch: {
        dateRangeFilter: {
            deep: true,
            handler(range) {
                if (!range.startDate || !range.endDate) return;
                this.loadTransactions();
            },
        },
    },

    mounted() {
        this.loadStatus();
        this.loadStats();
        this.loadTransactions();
    },

    beforeDestroy() {
        this.stopDpPolling();
    },

    methods: {
        validateRecipientNumber(phoneNumber) {
            this.recipientValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
        },
        validateFundingPhoneForSend(phoneNumber) {
            this.fundingPhoneForSendValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
        },
        validateFundPhoneNumber(phoneNumber) {
            this.fundPhoneValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
        },

        loadStatus() {
            axios.get('/api/dukakit-payments/settings').then(({ data }) => { this.status = data; });
        },
        loadStats() {
            this.loading.stats = true;
            axios.get('/api/dukakit-payments/stats')
                .then(({ data }) => { this.stats = data; })
                .catch(err => console.error(err))
                .finally(() => this.loading.stats = false);
        },
        loadTransactions() {
            this.loading.transactions = true;
            axios.get('/api/dukakit-payments/transactions', {
                params: {
                    per_page: 100,
                    start_date: this.dateRangeFilter.startDate ? this.toIsoDate(this.dateRangeFilter.startDate) : undefined,
                    end_date: this.dateRangeFilter.endDate ? this.toIsoDate(this.dateRangeFilter.endDate) : undefined,
                },
            })
                .then(({ data }) => { this.transactions = data.data || data; })
                .catch(err => console.error(err))
                .finally(() => this.loading.transactions = false);
        },

        clearFilters() {
            this.search = '';
            this.dateRangeFilter = { startDate: null, endDate: null };
            this.loadTransactions();
        },

        toIsoDate(date) {
            return new Date(date).toISOString().split('T')[0];
        },
        formatPickerDate(date) {
            return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },

        openSendModal() {
            this.sendForm = { recipient: '', amount: null, recipient_type: 'personal', account_reference: '', reason: '' };
            this.sendQuote = null;
            this.fundingRequired = null;
            this.$refs.sendModal.show();
        },
        openFundModal() {
            this.fundForm = { phone_number: '', amount: null };
            this.$refs.fundModal.show();
        },

        quote() {
            if (!this.sendForm.amount || this.sendForm.amount <= 0) { this.sendQuote = null; return; }
            axios.post('/api/dukakit-payments/send-money/quote', {
                amount: this.sendForm.amount,
                recipient_type: this.sendForm.recipient_type,
            }).then(({ data }) => { this.sendQuote = data; }).catch(() => { this.sendQuote = null; });
        },

        submitSend() {
            if (this.sendForm.recipient_type === 'personal' && this.recipientValidMessage) return;
            this.sending = true;
            this.fundingRequired = null;
            axios.post('/api/dukakit-payments/send-money', this.sendForm)
                .then(({ data }) => {
                    this.$refs.sendModal.hide();
                    this.startDpPolling(
                        `/api/dukakit-payments/send-money/${data.mpesa_b2c_id}/status`,
                        'Sending money — awaiting Safaricom confirmation...',
                        'Money sent successfully.'
                    );
                })
                .catch(err => {
                    if (err.response?.status === 422 && err.response.data?.status === 'insufficient_balance') {
                        this.fundingRequired = this.formatMoney(err.response.data.required);
                    } else {
                        this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Send Money failed' });
                    }
                })
                .finally(() => this.sending = false);
        },

        fundThenSend() {
            if (this.fundingPhoneForSendValidMessage || !this.fundingPhoneForSend) return;
            this.sending = true;
            axios.post('/api/dukakit-payments/send-money/fund-then-send', {
                funding_phone_number: this.fundingPhoneForSend,
                recipient: this.sendForm.recipient,
                amount: this.sendForm.amount,
                recipient_type: this.sendForm.recipient_type,
                account_reference: this.sendForm.account_reference,
                reason: this.sendForm.reason,
            })
                .then(({ data }) => {
                    this.$refs.sendModal.hide();
                    this.startDpPolling(
                        `/api/dukakit-payments/payments/${data.payment_id}/status`,
                        'Please check your phone and enter your M-Pesa PIN to confirm the funding payment...',
                        'Wallet funded — your Send Money request is now being processed.'
                    );
                })
                .catch(err => this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not initiate funding' }))
                .finally(() => this.sending = false);
        },

        submitFund() {
            if (this.fundPhoneValidMessage || !this.fundForm.phone_number) return;
            this.funding = true;
            axios.post('/api/dukakit-payments/fund-wallet', this.fundForm)
                .then(({ data }) => {
                    this.$refs.fundModal.hide();
                    this.startDpPolling(
                        `/api/dukakit-payments/payments/${data.payment_id}/status`,
                        'Please check your phone and enter your M-Pesa PIN to confirm the payment...',
                        'Payment received — your wallet has been credited.'
                    );
                })
                .catch(err => this.$toasts.push({ type: 'error', message: err.response?.data?.message || 'Could not initiate funding' }))
                .finally(() => this.funding = false);
        },

        /**
         * Shared poller for every async Dukakit Payments action on this page
         * (Fund Wallet, Fund & Send, Send Money) - all three used to either
         * show nothing further (Send Money: a toast then silence, even
         * though the real M-Pesa transfer had already completed by the time
         * anyone looked) or duplicate this same state machine per-flow.
         */
        startDpPolling(statusUrl, pendingMessage, completedMessage) {
            this.dpPollState = 'pending';
            this.dpPollMessage = '';
            this.dpPollPendingMessage = pendingMessage;
            this.$refs.dpProgressModal.show();
            this.stopDpPolling();

            this.dpPollInterval = setInterval(async () => {
                try {
                    const { data } = await axios.get(statusUrl);

                    if (data.status === 'completed') {
                        this.dpPollState = 'completed';
                        this.dpPollMessage = completedMessage;
                        this.stopDpPolling();
                        this.loadStats();
                        this.loadTransactions();
                        setTimeout(() => this.$refs.dpProgressModal.hide(), 3000);
                    } else if (data.status === 'failed' || data.status === 'rejected') {
                        this.dpPollState = 'failed';
                        this.dpPollMessage = data.result_desc || (data.status === 'rejected'
                            ? 'This payout was rejected during internal approval.'
                            : 'There was an issue with the payment.');
                        this.stopDpPolling();
                    } else if (data.status === 'awaiting_approval') {
                        // Money hasn't been sent to Safaricom yet - it's
                        // sitting behind the mandatory internal approval gate
                        // (see DukakitPayoutApprovalController), not stuck.
                        this.dpPollPendingMessage = 'Awaiting internal approval before this payout is submitted to M-Pesa...';
                    } else if (data.status === 'awaiting_liquidity') {
                        this.dpPollPendingMessage = 'Topping up the Working account balance before this payout is submitted...';
                    }
                    // 'processing' (and any other in-flight status) keeps the
                    // original pendingMessage as-is.
                } catch (e) {
                    console.error('Error checking Dukakit payment status:', e);
                }
            }, 5000);
        },

        stopDpPolling() {
            if (this.dpPollInterval) {
                clearInterval(this.dpPollInterval);
                this.dpPollInterval = null;
            }
        },

        copyPaybillInfo() {
            const text = `Paybill: ${this.status.paybill}\nAccount: ${this.status.account_number}`;
            navigator.clipboard?.writeText(text);
            this.$toasts.push({ type: 'success', message: 'Copied to clipboard' });
        },

        copyText(text) {
            navigator.clipboard?.writeText(text);
            this.$toasts.push({ type: 'success', message: 'Copied to clipboard' });
        },

        copyAllDetails(tx) {
            const text = (tx.details || []).map(row => `${row.label}: ${row.value}`).join('\n');
            this.copyText(text);
        },

        openDetailsModal(tx) {
            this.selectedTx = null;
            this.detailsLoading = true;
            this.$refs.detailsModal.show();
            axios.get(`/api/dukakit-payments/transactions/${tx.id}`)
                .then(({ data }) => { this.selectedTx = data; })
                .catch(() => {
                    this.$toasts.push({ type: 'error', message: 'Could not load transaction details' });
                    this.$refs.detailsModal.hide();
                })
                .finally(() => this.detailsLoading = false);
        },

        statusBadgeClass(tx) {
            const status = tx?.presented?.status;
            if (status === 'completed' || status === 'confirmed' || status === 'settled') return 'badge-primary';
            if (status === 'failed' || status === 'invalidated') return 'badge-danger';
            if (status === 'processing' || status === 'pending' || status === 'pending_match' || status === 'unassigned') return 'badge-dark';
            return 'badge-secondary';
        },

        formatMoney(v) {
            return Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatFees(tx) {
            const total = Number(tx.safaricom_cost || 0) + Number(tx.dukakit_fee || 0);
            return total > 0 ? 'KSh ' + this.formatMoney(total) : '—';
        },
        formatDate(d) {
            return d ? new Date(d).toLocaleString() : '';
        },
        formatType(type) {
            return (type || '').replace(/_/g, ' ');
        },
        typeBadgeClass(type) {
            if (type === 'payout' || type === 'settlement') return 'badge-danger';
            if (type === 'stk_collection' || type === 'c2b_collection' || type === 'wallet_funding') return 'badge-primary';
            return 'badge-secondary';
        },
    },
};
</script>

<style scoped>
.skeleton {
    background: #f1f5f9;
    animation: shimmer 1.4s ease-in-out infinite;
}

@keyframes shimmer {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.4; }
}

.spin { display: inline-block; animation: spin 0.8s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.custom-range-picker ::v-deep .reportrange-text {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    height: auto !important;
}

.dp-search-input:focus { outline: none; }

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #94a3b8;
    font-size: 13px;
}

.empty-state i { font-size: 32px; margin-bottom: 4px; opacity: 0.6; }

.table-container { overflow-x: auto; }

.dp-table {
    width: 100%;
    border-collapse: collapse;
}

.dp-table thead { border-bottom: 2px solid #e2e8f0; }

.dp-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
}

.dp-table td { padding: 14px 16px; }

.dp-row { border-bottom: 1px solid #f1f5f9; cursor: pointer; transition: background 0.2s; }
.dp-row:hover { background: #f8fafc; }

.badge.pill {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    text-transform: capitalize;
}

.detail-table { width: 100%; }
.detail-table th {
    text-align: left;
    padding: 8px 12px 8px 0;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
    vertical-align: top;
}
.detail-table td {
    padding: 8px 0;
    font-size: 13px;
    color: #0f172a;
    border-bottom: 1px solid #f1f5f9;
}
</style>
